/**
 * CareLogPanel (#721) — the dog's meal / medication log, with the streak on
 * top and a one-tap way to add today's outcome.
 *
 * Counter Test: at 6:05 PM a kennel tech taps the dog, sees "Ate none at dinner
 * 2 days running" without scrolling, taps Dinner → Ate none, and it is
 * recorded with her name on it. Nobody types a UUID and nobody edits a row.
 *
 * APPEND-ONLY, and the UI says so out loud. There is no edit control and no
 * delete control, because the server has neither route: a correction is a
 * new entry, and the superseded one stays in the history. That is what makes
 * this an administration record (#553 / PACFA) instead of a status field.
 *
 * All five states (DESIGN-PHILOSOPHY §Every component ships all five):
 *  · loading — skeleton rows, never a blank panel or a spinner wall (§4);
 *  · error   — what happened + Retry, cold-start-safe, no technology names;
 *  · empty   — teaching copy + the primary action, never a bare "0" (§2);
 *  · default — the streak, the form, the history;
 *  · stale   — the honesty state (§5): when the log could not be re-read
 *              after a write, the panel says the list may be behind rather
 *              than showing a confident-looking list that is not current.
 *
 * Parent visibility: the "Share with the owner" tick is only offered when
 * the TENANT has turned the care log on, and the panel says so when it has
 * not — offering a control that silently does nothing is a lie (§5). The
 * tick is never offered for medications at all: those never reach an owner
 * in v1, and the server enforces that in the serializer regardless of what
 * this component sends.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  careLogApi,
  humanizeApiError,
  type CareLogResponse,
  type CreateCareEventBody,
  type MealOutcome,
  type MealSlot,
  type LogMedicationOutcome,
  type MedicationSlot,
  type StaffCareEvent,
} from '../../lib/api.js';
import { CareStreakBadge } from './CareStreakBadge.js';
import { AteNoneBadge, parseAteNone, streakRepeatsAteNone } from './AteNoneBadge.js';
// Sawyer 08 meds-feeding (C-11) — one word per time slot across the app.
import { SLOT_LABEL as CARE_SLOT_LABEL } from '../../lib/care-labels.js';
import './care-log.css';

export interface CareLogPanelProps {
  apiUrl: string;
  dogId: string;
  dogName: string;
  /**
   * Whether this staffer holds `pets.notes:write:all`. False ⇒ read-only:
   * we do not render an affordance the server would 403 (the #484 lesson —
   * a button that fails is worse than no button).
   */
  canLog: boolean;
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; data: CareLogResponse; stale: boolean };

const MEAL_SLOTS: readonly MealSlot[] = ['breakfast', 'lunch', 'dinner', 'snack'];
const MEAL_OUTCOMES: readonly MealOutcome[] = ['ate_all', 'ate_partial', 'refused'];
const MED_SLOTS: readonly MedicationSlot[] = [
  'am',
  'midday',
  'pm',
  'bedtime',
  'as_needed',
];
const MED_OUTCOMES: readonly LogMedicationOutcome[] = ['given', 'refused', 'missed'];

/** Staff-facing copy. Never show the raw enum (§Anti-patterns). */
const SLOT_LABEL: Record<string, string> = {
  breakfast: 'Breakfast',
  lunch: 'Lunch',
  dinner: 'Dinner',
  snack: 'Snack',
  // Medication times read exactly as on the Meds & feeding report and the
  // med pass (AM / Noon / PM / Bedtime / As needed (PRN)).
  ...CARE_SLOT_LABEL,
};

/**
 * Sawyer #1295 — a MEAL the dog refused reads "Ate none" (the desk's words);
 * a refused MEDICATION still reads "Refused". Stored value unchanged.
 */
function outcomeLabel(kind: string, outcome: string): string {
  if (kind === 'meal' && outcome === 'refused') return 'Ate none';
  return OUTCOME_LABEL[outcome] ?? outcome;
}

const OUTCOME_LABEL: Record<string, string> = {
  ate_all: 'Ate it all',
  ate_partial: 'Ate some',
  refused: 'Refused',
  given: 'Took it',
  missed: 'Missed',
  // Sawyer 08 meds-feeding (A2) — the med pass's two statuses, never a raw enum.
  prepared: 'Prepared',
  partially_administered: 'Partly given',
  // The legacy backfill value: a meal we know was served and nothing more.
  // Saying "Logged (no outcome recorded)" is the honest rendering — calling
  // it "Ate it all" would invent a health signal (§5).
  logged: 'Logged — outcome not recorded',
};

/** Which status colour a history row carries. Absence of colour is fine. */
function outcomeTone(outcome: string): 'bad' | 'watch' | 'ok' | 'unknown' {
  if (outcome === 'refused' || outcome === 'missed') return 'bad';
  if (outcome === 'ate_partial' || outcome === 'partially_administered') return 'watch';
  if (outcome === 'ate_all' || outcome === 'given') return 'ok';
  return 'unknown';
}

/** "Wed 6:12 PM" — a human time, never a raw ISO string (§Anti-patterns). */
function formatWhen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    weekday: 'short',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function HistoryRow({ event }: { event: StaffCareEvent }): JSX.Element {
  const tone = outcomeTone(event.outcome);
  return (
    <li
      className={`ferro-care-log__row ferro-care-log__row--${tone}`}
      data-testid="care-log-row"
      data-outcome={event.outcome}
    >
      <span className="ferro-care-log__row-slot">
        {SLOT_LABEL[event.slot] ?? event.slot}
      </span>
      <span className="ferro-care-log__row-outcome">
        {outcomeLabel(event.kind, event.outcome)}
      </span>
      <span className="ferro-care-log__row-meta">
        {formatWhen(event.occurredAt)}
        {event.staffName ? ` · ${event.staffName}` : ''}
        {event.kind === 'medication' ? ' · medication' : ''}
        {event.parentVisible ? ' · shared with owner' : ''}
      </span>
      {event.notes ? (
        <span className="ferro-care-log__row-notes">{event.notes}</span>
      ) : null}
    </li>
  );
}

export function CareLogPanel({
  apiUrl,
  dogId,
  dogName,
  canLog,
}: CareLogPanelProps): JSX.Element {
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [kind, setKind] = useState<'meal' | 'medication'>('meal');
  const [slot, setSlot] = useState<string>('dinner');
  const [outcome, setOutcome] = useState<string>('ate_all');
  const [notes, setNotes] = useState('');
  const [shareWithOwner, setShareWithOwner] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    setState({ kind: 'loading' });
    const res = await careLogApi.get(apiUrl, dogId, 30);
    if (res.error || !res.data) {
      setState({ kind: 'error', message: humanizeApiError(res.error ?? '') });
      return;
    }
    setState({ kind: 'ready', data: res.data, stale: false });
  }, [apiUrl, dogId]);

  useEffect(() => {
    void load();
  }, [load]);

  // Switching kind resets slot/outcome to that taxonomy's defaults, so an
  // impossible pair ("medication / ate_all") is never even selectable.
  const switchKind = (next: 'meal' | 'medication'): void => {
    setKind(next);
    setSlot(next === 'meal' ? 'dinner' : 'pm');
    setOutcome(next === 'meal' ? 'ate_all' : 'given');
    if (next === 'medication') setShareWithOwner(false);
  };

  const submit = async (): Promise<void> => {
    setSaving(true);
    setSaveError(null);
    const body: CreateCareEventBody =
      kind === 'meal'
        ? {
            kind: 'meal',
            slot: slot as MealSlot,
            outcome: outcome as MealOutcome,
            notes: notes.trim() === '' ? undefined : notes.trim(),
            parentVisible: shareWithOwner,
          }
        : {
            kind: 'medication',
            slot: slot as MedicationSlot,
            outcome: outcome as LogMedicationOutcome,
            notes: notes.trim() === '' ? undefined : notes.trim(),
          };
    const res = await careLogApi.append(apiUrl, dogId, body);
    setSaving(false);
    if (res.error || !res.data) {
      setSaveError(humanizeApiError(res.error ?? ''));
      return;
    }
    setNotes('');
    setShareWithOwner(false);
    // Re-read so the streak and the list agree (§1 one glance, one truth —
    // two sources that can disagree is the defect). If the re-read fails we
    // say the list may be behind rather than pretending it is current.
    const fresh = await careLogApi.get(apiUrl, dogId, 30);
    if (fresh.error || !fresh.data) {
      setState((prev) =>
        prev.kind === 'ready' ? { ...prev, stale: true } : prev,
      );
      return;
    }
    setState({ kind: 'ready', data: fresh.data, stale: false });
  };

  const slots = kind === 'meal' ? MEAL_SLOTS : MED_SLOTS;
  const outcomes = kind === 'meal' ? MEAL_OUTCOMES : MED_OUTCOMES;
  const tenantSharingOn =
    state.kind === 'ready' && state.data.tenantParentVisibilityEnabled;

  return (
    <section className="ferro-care-log" data-testid="care-log-panel">
      <header className="ferro-care-log__header">
        <h3 className="ferro-care-log__title">Meals &amp; medications</h3>
        {state.kind === 'ready' ? (
          <>
            {/* Sawyer #1295 — today's "Ate none" meals, one badge. */}
            <AteNoneBadge ateNone={state.data.ateNone} size="md" dogName={dogName} testId="care-log-ate-none" />
            {streakRepeatsAteNone(state.data.worstStreak?.slot, parseAteNone(state.data.ateNone)) ? null : (
              <CareStreakBadge
                label={state.data.streakLabel}
                days={state.data.worstStreak?.days ?? 0}
                size="md"
                dogName={dogName}
                testId="care-log-streak"
              />
            )}
          </>
        ) : null}
      </header>

      {/* LOADING — skeleton, never blank (§4). */}
      {state.kind === 'loading' ? (
        <div
          className="ferro-care-log__skeleton"
          aria-busy="true"
          data-testid="care-log-loading"
        >
          <span className="ferro-care-log__skeleton-line" />
          <span className="ferro-care-log__skeleton-line ferro-care-log__skeleton-line--short" />
        </div>
      ) : null}

      {/* ERROR — what happened + how to recover; no technology names. */}
      {state.kind === 'error' ? (
        <div className="ferro-care-log__error" role="alert" data-testid="care-log-error">
          <p className="ferro-care-log__error-msg">{state.message}</p>
          <button
            type="button"
            className="ferro-btn ferro-btn--ghost"
            onClick={() => void load()}
            data-testid="care-log-retry"
          >
            Try again
          </button>
        </div>
      ) : null}

      {/* STALE — the honesty state (§5). */}
      {state.kind === 'ready' && state.stale ? (
        <p className="ferro-care-log__stale" role="status" data-testid="care-log-stale">
          Saved. This list may be a moment behind —{' '}
          <button
            type="button"
            className="ferro-care-log__inline-btn"
            onClick={() => void load()}
          >
            refresh
          </button>
          .
        </p>
      ) : null}

      {state.kind === 'ready' ? (
        <>
          {/* WRITE — the primary action of this panel. */}
          {canLog ? (
            <div className="ferro-care-log__form" data-testid="care-log-form">
              <div
                className="ferro-care-log__kind"
                role="group"
                aria-label="What are you logging?"
              >
                {(['meal', 'medication'] as const).map((k) => (
                  <button
                    key={k}
                    type="button"
                    className={`ferro-care-log__chip${kind === k ? ' ferro-care-log__chip--on' : ''}`}
                    aria-pressed={kind === k}
                    onClick={() => switchKind(k)}
                    data-testid={`care-log-kind-${k}`}
                  >
                    {k === 'meal' ? 'Meal' : 'Medication'}
                  </button>
                ))}
              </div>

              <div className="ferro-care-log__field">
                <label className="ferro-care-log__label" htmlFor="care-log-slot">
                  {kind === 'meal' ? 'Which meal' : 'When'}
                </label>
                <select
                  id="care-log-slot"
                  className="ferro-care-log__select"
                  value={slot}
                  onChange={(e) => setSlot(e.target.value)}
                  data-testid="care-log-slot"
                >
                  {slots.map((s) => (
                    <option key={s} value={s}>
                      {SLOT_LABEL[s]}
                    </option>
                  ))}
                </select>
              </div>

              <div className="ferro-care-log__field">
                <label className="ferro-care-log__label" htmlFor="care-log-outcome">
                  What happened
                </label>
                <select
                  id="care-log-outcome"
                  className="ferro-care-log__select"
                  value={outcome}
                  onChange={(e) => setOutcome(e.target.value)}
                  data-testid="care-log-outcome"
                >
                  {outcomes.map((o) => (
                    <option key={o} value={o}>
                      {outcomeLabel(kind, o)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="ferro-care-log__field ferro-care-log__field--wide">
                <label className="ferro-care-log__label" htmlFor="care-log-notes">
                  Note (optional)
                </label>
                <input
                  id="care-log-notes"
                  className="ferro-care-log__input"
                  value={notes}
                  maxLength={500}
                  placeholder="Left half the bowl"
                  onChange={(e) => setNotes(e.target.value)}
                  data-testid="care-log-notes"
                />
              </div>

              {/* Share-with-owner: meals only, and only when the tenant has
                  turned the parent-facing log on. Otherwise say so — an
                  inert tick box is a lie (§5). */}
              {kind === 'meal' ? (
                tenantSharingOn ? (
                  <label
                    className="ferro-care-log__share"
                    data-testid="care-log-share"
                  >
                    <input
                      type="checkbox"
                      checked={shareWithOwner}
                      onChange={(e) => setShareWithOwner(e.target.checked)}
                      data-testid="care-log-share-input"
                    />
                    <span>Share this with {dogName}&apos;s owner</span>
                  </label>
                ) : (
                  <p
                    className="ferro-care-log__share-off"
                    data-testid="care-log-share-off"
                  >
                    Owners can&apos;t see the care log yet — an owner turns
                    that on in settings.
                  </p>
                )
              ) : (
                <p
                  className="ferro-care-log__share-off"
                  data-testid="care-log-share-med"
                >
                  Medication entries stay staff-only.
                </p>
              )}

              {saveError ? (
                <p
                  className="ferro-care-log__error-msg"
                  role="alert"
                  data-testid="care-log-save-error"
                >
                  {saveError}
                </p>
              ) : null}

              <button
                type="button"
                className="ferro-btn ferro-btn--primary ferro-care-log__save"
                onClick={() => void submit()}
                disabled={saving}
                data-testid="care-log-save"
              >
                {saving ? 'Saving…' : 'Log it'}
              </button>
              <p className="ferro-care-log__appendonly">
                Entries can&apos;t be edited or deleted. To correct one, log
                the right answer — both stay on the record.
              </p>
            </div>
          ) : null}

          {/* EMPTY — teaching copy, never a bare zero (§2). */}
          {state.data.careEvents.length === 0 ? (
            <p className="ferro-care-log__empty" data-testid="care-log-empty">
              {canLog
                ? `Nothing logged for ${dogName} yet. Record the next meal and the history starts here.`
                : `Nothing logged for ${dogName} yet.`}
            </p>
          ) : (
            <ul className="ferro-care-log__list" data-testid="care-log-list">
              {state.data.careEvents.map((e) => (
                <HistoryRow key={e.id} event={e} />
              ))}
            </ul>
          )}
        </>
      ) : null}
    </section>
  );
}
